import { useEffect, useState } from 'react';
import { Accordion, AccordionItem, Chip } from '@nextui-org/react';
import { MdOutlineKeyboardArrowRight, MdOutlineKeyboardArrowDown } from 'react-icons/md';

import type { SkillIndicator, FeedbackGoal as FeedbackGoalType } from '@/types/simulations';

import { scoreLabels } from '@/_lib/constants';
import FeedbackGoal from './feedback-goal';

const itemClasses = {
  title: 'font-semibold text-base',
  trigger: 'items-start pb-2',
  subtitle: 'pt-2'
};

export const getChipColorsByScore = (value: number) => {
  const colors: Record<number, string> = {
    0: 'text-neutral-600 bg-neutral-100',
    25: 'text-darkOrange bg-red-100',
    50: 'text-primary bg-yellow-100',
    75: 'text-blue-600 bg-blue-100',
    100: 'text-brandPrimaryGreen bg-green-100'
  };

  const score = value ?? 100;
  return colors[score] || 'text-brandPrimaryGreen bg-green-100';
};

export const getScoreLabel = (score: number) =>
  scoreLabels.find(entry => score === entry.score)?.label ?? scoreLabels[scoreLabels.length - 1]?.label;

const SkillSection = ({
  skills,
  onSelectKpi,
  selectedKpi,
  goals
}: {
  skills: {
    name: string;
    summary: string;
    indicators: SkillIndicator[];
  }[];
  onSelectKpi: any;
  selectedKpi: SkillIndicator | undefined;
  goals: FeedbackGoalType[];
}) => {
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  const handleSelectionChange = (keys: any) => {
    setOpenItems(keys);
  };

  useEffect(() => {
    if (openItems.size === 0) onSelectKpi(null);
  }, [openItems]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex-1 p-4 bg-neutral-50 border-1 border-cardBorderColor rounded-xl flex flex-col gap-4">
        <h3 className="text-xl leading-8 font-semibold">How did your conversation go?</h3>

        <FeedbackGoal goals={goals} />

        <Accordion
          variant="splitted"
          className="gap-4 px-0"
          itemClasses={itemClasses}
          selectedKeys={openItems}
          onSelectionChange={handleSelectionChange}
        >
          {skills?.map((skill, index) => {
            return (
              <AccordionItem
                key={index}
                aria-label={skill.name}
                title={skill.name}
                subtitle={skill.summary}
                indicator={({ isOpen }) => (
                  <div className="p-1 rounded-md bg-lightPurple-50">
                    {isOpen ? (
                      <MdOutlineKeyboardArrowRight size={16} className="text-black" />
                    ) : (
                      <MdOutlineKeyboardArrowDown size={16} className="text-black" />
                    )}
                  </div>
                )}
              >
                <div className="flex flex-col divide-y divide-neutral-100">
                  {skill.indicators.map((item, idx) => {
                    return (
                      <div
                        key={idx}
                        className={`flex justify-between items-center p-2 rounded-md cursor-pointer hover:bg-lightPurple-50 ${selectedKpi?.name === item.name && 'bg-violet-200'}`}
                        onClick={e => onSelectKpi(item)}
                      >
                        <p className="text-sm font-normal">{item.name}</p>
                        <div className="flex gap-2 items-center">
                          <Chip className={getChipColorsByScore(item.score)}>{getScoreLabel(item.score)}</Chip>
                          <MdOutlineKeyboardArrowRight size={16} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>
    </div>
  );
};

export default SkillSection;
